import { type Agency, type Merchant } from '../../../common/types';
import { useGetMerchantsForAgencyQuery, useGetMerchantsQuery } from '../merchant.api-slice';
import AddMerchant from '../components/AddMerchant';
import ApproveMerchant from '../components/ApproveMerchant';
import Breadcrumb from '../../../components/Breadcrumb';
import DeleteMerchant from '../components/DeleteMerchant';
import EditMerchant from '../components/EditMerchant';
import { getAgencyMerchantStoreBasedOnScopeAndRole } from '../../../common/helpers';
import { Link } from 'react-router-dom';
import ListMerchants from '../components/ListMerchants';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Pagination from '../../../components/Pagination';
import { skipToken } from '@reduxjs/toolkit/query';
import SuccessMessage from '../../../components/success-message';
import { useAuth } from '../../../app/hooks/useAuth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  scope?: 'all' | 'agency';
  agency?: Agency;
  filter?: { pending: boolean };
}

const ListMerchantsPage: React.FC<IProps> = ({ scope = 'all', filter, agency }) => {
  const { user } = useAuth();
  const [editingItem, setEditingItem] = useState<Merchant | null>(null);
  const [deletingItem, setDeletingItem] = useState<Merchant | null>(null);
  const [approvingItem, setApprovingItem] = useState<Merchant | null>(null);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const { data: merchants, isLoading } =
    scope === 'agency'
      ? useGetMerchantsForAgencyQuery(agency != null ? { page, id: agency.id } : skipToken)
      : useGetMerchantsQuery({ page, pendingOnly: filter?.pending === true });
  const { t } = useTranslation();
  const parentContext = getAgencyMerchantStoreBasedOnScopeAndRole({ loggedInUser: user, scope, agency });

  const toggleAddModal = (): void => {
    setOpenAddModal((prevState) => !prevState);
  };

  const toggleEditModal = (): void => {
    if (openEditModal) {
      setEditingItem(null);
    }
    setOpenEditModal(!openEditModal);
  };

  const toggleDeleteModal = (): void => {
    if (openDeleteModal) {
      setDeletingItem(null);
    }
    setOpenDeleteModal(!openDeleteModal);
  };

  const toggleApproveModal = (): void => {
    if (openApproveModal) {
      setApprovingItem(null);
    }
    setOpenApproveModal(!openApproveModal);
  };

  const handleEditingItem = (item: Merchant | null): void => {
    setEditingItem(item);
  };

  const handleDeletingItem = (item: Merchant | null): void => {
    setDeletingItem(item);
  };

  const handleApprovingItem = (item: Merchant | null): void => {
    setApprovingItem(item);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="pb-4">
        <h1>{t('merchants.list.title')}</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('merchants.list.breadcrumbLabel') }]}
        />
      </div>

      <div className="w-full flex flex-col md:flex-row md:justify-between md:items-center space-y-2">
        <h1 className="text-2xl">{t('merchants.list.heading')}</h1>
        <div className="flex justify-items-center md:justify-between md:items-center gap-x-2">
          {filter?.pending === true && (
            <Link to="/admin/merchants">
              <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">
                {t('merchants.list.listLinkLabel')}
              </button>
            </Link>
          )}
          {filter?.pending !== true && (
            <>
              <Link to="/admin/merchants/pending-requests">
                <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">
                  {t('merchants.list.pendingListLabel')}
                </button>
              </Link>
              <button onClick={toggleAddModal} className="text-white ml-4 px-5 py-2 rounded-lg bg-[#1D8A43]">
                {t('merchants.list.inviteLabel')}
              </button>
            </>
          )}
        </div>
      </div>

      <AddMerchant
        isOpen={openAddModal}
        toggleModal={toggleAddModal}
        agencyInContext={agency}
        parentContext={parentContext}
      />

      <EditMerchant isOpen={openEditModal} toggleEditModal={toggleEditModal} item={editingItem} />

      <DeleteMerchant isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} item={deletingItem} />

      <ApproveMerchant isOpen={openApproveModal} toggleApproveModal={toggleApproveModal} item={approvingItem} />

      {merchants?.data == null ||
        (merchants.data.length === 0 && <SuccessMessage message={t('merchants.list.empty')} type="error" />)}

      {merchants?.data != null && merchants.data.length > 0 && (
        <>
          <ListMerchants
            items={merchants.data}
            filter={{ pending: filter?.pending === true }}
            toggleEditModal={toggleEditModal}
            toggleDeleteModal={toggleDeleteModal}
            toggleApproveModal={toggleApproveModal}
            handleEditingItem={handleEditingItem}
            handleDeletingItem={handleDeletingItem}
            handleApprovingItem={handleApprovingItem}
          />
          <Pagination pageData={merchants.meta} handlePageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default ListMerchantsPage;
